<template>
  <v-card>
    <v-card-title>
      {{ title }}
    </v-card-title>
    <v-card-text>
      <v-container class="pa-0">
        <v-row>
          <v-col>
            <v-autocomplete
              v-model="form.paymentMilestones"
              :items="milestonesFilteredByCurrency"
              item-text="name"
              :item-value="item => `${item.parentMilestoneId}-${item.id}`"
              outlined
              multiple
              return-object
              chips
              small-chips
              deletable-chips
              :label="$t('payment.application.selectMilestones')"
              @change="$v.form.paymentMilestones.$touch()"
              :error-messages="errorMessages('paymentMilestones')"
              :filter="filterMilestones"
              :disabled="partialEdit"
            >
              <template v-slot:selection="data">
                <v-tooltip
                  bottom
                  :disabled="!data.item.description"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      v-bind="{...data.attrs, ...attrs}"
                      v-on="on"
                      close
                      @click:close="handleRemoveMilestone(data.item)"
                      :disabled="partialEdit"
                    >
                      {{ data.item.name }}
                    </v-chip>
                  </template>
                  <span> {{ data.item.description }} </span>
                </v-tooltip>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content :key="item.key">
                  <v-list-item-title>{{item.name}}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.description }}
                    ({{ item.paymentAmountCents | formatCurrency({ key: item.currency.key, value: 1 }) }})
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row
          dense
          v-if="form.paymentMilestones.length"
        >
          <v-spacer />
          <v-col cols="6">
            <p class="text-right">
              {{ $t('payment.application.totalAmount') }}
              <PricesDisplay
                :prices="paymentCentsPerCurrency"
                :contractCurrencies="currencies"
                :chosenCurrency="chosenCurrency"
                :itemCurrency="chosenCurrency"
              />
            </p>
          </v-col>
        </v-row>
        <v-row
          no-gutters
          v-if="form.paymentMilestones.length"
        >
          <v-col>
            <v-simple-table :key="key">
              <thead>
                <tr>
                  <th class="text-left">{{ $t('common.name') }}</th>
                  <th class="text-left">{{ $t('payment.application.amount') }}</th>
                  <th class="text-left">{{ $t('payment.application.availablePartAmount') }}</th>
                  <th class="text-left">{{ $t('payment.application.partPayment') }}</th>
                  <th class="text-left">{{ $t('payment.application.partAmount') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in form.paymentMilestones"
                  :key="item.name"
                >
                  <td>{{ item.name }}</td>
                  <td>{{ item.paymentAmountCents | formatCurrency({ key: item.currency.key, value: 1 }) }}</td>
                  <td>
                    {{ item.amountToPay | formatCurrency({ key: item.currency.key, value: 1 }) }}
                    ({{ item.percentToPay | toFixed }} %)
                  </td>
                  <td>
                    <v-text-field
                      :label="$t('payment.application.partOfPayment')"
                      dense
                      outlined
                      :min="0"
                      :max="100"
                      hide-details
                      append-icon="mdi-percent-outline"
                      :value="formatPaymentPart(item.paymentPart)"
                      @input="updatePaymentPart($event, item, index)"
                      :error="isAmountToHigh(item) != null"
                    />
                  </td>
                  <td>
                    <VfCurrencyInput
                      v-model="item.partAmountCents"
                      :currency="chosenCurrency"
                      :label="$t('payment.application.partAmount')"
                      @input="handlePaymentAmountChange(item)"
                      dense
                      hide-details
                      outlined
                      :error="isAmountToHigh(item) != null"
                    />
                    <div
                      v-if="isAmountToHigh(item)"
                      class="error--text error-message"
                    >
                      {{ isAmountToHigh(item) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              v-model="form.workflow"
              :items="workflows"
              item-text="name"
              item-value="id"
              outlined
              dense
              return-object
              persistent-hint
              :hint="$t('payment.application.workflowHint')"
              :label="$t('payment.application.selectWorkflow')"
              @change="$v.form.workflow.$touch()"
              :error-messages="errorMessages('workflow')"
              :disabled="partialEdit"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content :key="item.key">
                  <v-list-item-title>{{item.name}}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.name"
              :label="$t('payment.application.name')"
              dense
              outlined
              counter="32"
              @change="$v.form.name.$touch()"
              :error-messages="errorMessages('name')"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.paymentCertificate"
              :label="$t('payment.application.certificate')"
              dense
              outlined
              @change="$v.form.paymentCertificate.$touch()"
              :error-messages="errorMessages('paymentCertificate')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.invoiceReference"
              :label="$t('payment.application.invoiceRef')"
              dense
              outlined
              @change="$v.form.invoiceReference.$touch()"
              :error-messages="errorMessages('invoiceReference')"
            />
          </v-col>
          <v-col>
            <vf-date-input
              v-model="form.applicationDate"
              :label="$t('payment.application.date')"
              outlined
              dense
              @change="$v.form.applicationDate.$touch()"
              :error-messages="errorMessages('applicationDate')"
              :disabled="partialEdit"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="form.description"
              :label="$t('common.description')"
              dense
              outlined
              counter="128"
              rows="2"
              @change="$v.form.description.$touch()"
              :error-messages="errorMessages('description')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.comment"
              :label="$t('payment.application.comment')"
              dense
              outlined
              counter="120"
              @change="$v.form.comment.$touch()"
              :error-messages="errorMessages('comment')"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        rounded
        dark
        dense
        outlined
        color="primary"
        @click="cancel"
      >
        {{ $t('common.cancel') }}
      </v-btn>
      <SubmitButton @click="submit" />
    </v-card-actions>
  </v-card>
</template>

<script>
import { errorMessageGenerator, paymentApplicationModel } from '@/validators'
import cloneDeep from 'lodash/cloneDeep'
import { prepareSelectedMilestonesItem } from '@/services'
import { DEFAULT_PAYMENT_APPLICATION } from '@/helpers'

export default {
  name: 'PaymentApplicationForm',
  props: {
    title: String,
    payment: {
      type: Object,
      default: () => DEFAULT_PAYMENT_APPLICATION
    },
    workflows: Array,
    baseCurrency: Object,
    chosenCurrency: Object,
    rawMilestones: Array,
    processedMilestones: Array,
    paymentApplications: Array,
    currencies: Array
  },
  data () {
    return {
      applicationDateMenu: false,
      form: this.preparePayment(this.payment),
      paymentCentsPerCurrency: this.calculateCentsPerCurrency(this.payment.paymentMilestones),
      key: 0
    }
  },
  created () {
    this.$eventBus.$on('CLICK_OUTSIDE_MODAL', () => {
      this.cancel()
    })
  },
  computed: {
    partialEdit () {
      return this.form.workflowActions && !!this.form.workflowActions.length
    },
    paymentMilestones () {
      return this.form.paymentMilestones
    },
    selectedCurrency () {
      return this.paymentMilestones.length >= 1 ? this.paymentMilestones[0].currency : null
    },
    milestonesFilteredByCurrency () {
      if (this.selectedCurrency) {
        return this.disableMilestonesForCurrency(this.processedMilestones, this.selectedCurrency)
      }
      return this.processedMilestones
    },
    contractId () {
      return this.$route.params.contractId
    }
  },
  methods: {
    disableMilestonesForCurrency (milestones, currency) {
      return milestones.map(item => {
        if (item.currency && item.currency.key !== currency.key) {
          return {
            ...item,
            disabled: true
          }
        }
        return item
      })
    },
    calculateCentsPerCurrency (milestones) {
      const centsPerCurrency = milestones.reduce((acc, current) => {
        const currency = current.currency
        acc[currency.key] = acc[currency.key] ? acc[currency.key] + current.partAmountCents : current.partAmountCents
        acc.sum += current.partAmountCents * currency.value
        return acc
      }, { [this.baseCurrency.key]: 0, sum: 0 })
      const centsPerCurrencyArray = Object.entries(centsPerCurrency)
      return centsPerCurrencyArray.length > 2 ? Object.fromEntries(centsPerCurrencyArray.filter(([key, value]) => value !== 0)) : centsPerCurrency
    },
    filterMilestones (item, queryText) {
      return (
        !item.header &&
        ((item.description &&
          item.description.toUpperCase().includes(queryText.toUpperCase())) ||
          (item.name &&
            item.name.toUpperCase().includes(queryText.toUpperCase())))
      )
    },
    handleRemoveMilestone (item) {
      const index = this.form.paymentMilestones.findIndex(
        (i) =>
          i.id === item.id && i.parentMilestoneId === item.parentMilestoneId
      )
      if (index >= 0) this.form.paymentMilestones.splice(index, 1)
    },
    preparePayment (payment) {
      const newPayment = { ...payment } || { ...DEFAULT_PAYMENT_APPLICATION }
      const currency = payment.paymentMilestones.length >= 1 ? payment.paymentMilestones[0].currency : this.baseCurrency
      newPayment.workflow = newPayment.workflow || (this.workflows ? this.workflows[0] : null)
      return {
        ...newPayment,
        paymentAmountCents: payment.amountCents ? payment.amountCents[currency.key] || payment.amountCents : 0,
        paymentMilestones: !payment
          ? []
          : payment.paymentMilestones.map((item) => {
            return prepareSelectedMilestonesItem(this.paymentApplications, item, this.currencies)
          })
      }
    },
    isAmountToHigh (item) {
      const originalItem = this.getOriginalItem(item)
      const maxAllowedAmount = item.amountToPay + (originalItem.partAmountCents ?? 0)

      if ((item.paymentAmountCents < 0 && item.partAmountCents > 0) ||
          (item.paymentAmountCents > 0 && item.partAmountCents < 0)) {
        return this.$t('payment.application.moreThanMilestoneAmount')
      }

      if (item.paymentAmountCents < 0) {
        return item.partAmountCents < maxAllowedAmount
          ? this.$t('payment.application.moreThanMilestoneAmount')
          : null
      }

      return item.partAmountCents > maxAllowedAmount
        ? this.$t('payment.application.moreThanMilestoneAmount')
        : null
    },
    formatPaymentPart (value) {
      const parts = value.toString().split('.')

      if (parts.length === 2) {
        const fractionalLength = parts[1].length
        if (fractionalLength === 1) {
          return value.toFixed(1)
        } else if (fractionalLength >= 2) {
          return value.toFixed(2)
        } else if (fractionalLength === 3) {
          return value.toFixed(3)
        } else if (fractionalLength >= 4) {
          return value.toFixed(4)
        }
      }

      return parseFloat(value).toFixed(0)
    },
    updatePaymentPart (value, item, index) {
      const rawValue = value.replace(',', '.')
      const formattedValue = parseFloat(rawValue)
      if (!isNaN(formattedValue)) {
        this.$set(item, 'paymentPart', formattedValue)
        this.handlePaymentPartChange(item, index)
      }
    },
    handlePaymentPartChange (item, index) {
      const originalItem = this.getOriginalItem(item)
      const percentToPay = (Number(item.percentToPay) || 0) + (Number(originalItem.paymentPart) || 0)

      if (Number(item.paymentPart) <= percentToPay) {
        item.partAmountCents = (Number(item.paymentAmountCents) * (Number(item.paymentPart) / 100))
        this.updateFormPaymentAmountCents()
      } else {
        item.paymentPart = percentToPay
        item.partAmountCents = (Number(item.paymentAmountCents) * (Number(item.paymentPart) / 100))
        this.form.paymentMilestones[index] = item
      }
    },
    handlePaymentAmountChange (item, index) {
      const paymentPart = (item.partAmountCents / item.paymentAmountCents) * 100
      item.paymentPart = (Number(paymentPart.toFixed(2)))

      const originalItem = this.getOriginalItem(item)
      const maxAllowedAmount = item.amountToPay + (originalItem.partAmountCents ?? 0)

      const isValid = item.paymentAmountCents < 0
        ? Number(item.partAmountCents) >= maxAllowedAmount
        : Number(item.partAmountCents) <= maxAllowedAmount

      if (isValid) {
        this.updateFormPaymentAmountCents()
        this.form.paymentMilestones[index] = item
      }
    },
    getOriginalItem (item) {
      const original = this.payment && this.payment.paymentMilestones
        ? this.payment.paymentMilestones.find(i => i.id === item.id)
        : {}
      return original ? prepareSelectedMilestonesItem(this.paymentApplications, original, this.currencies) : {}
    },
    updateFormPaymentAmountCents () {
      this.form.paymentAmountCents = this.form.paymentMilestones.reduce(
        (acc, curr) => acc + (Number(curr.partAmountCents) || 0) * (Number(curr.currency.value) || 1),
        0
      )
    },
    errorMessages (key) {
      return errorMessageGenerator(this.$v.form[key], key)
    },
    cancel () {
      this.$v.$reset()
      this.$eventBus.$emit('CLOSE_MODAL')
      this.form = cloneDeep(this.preparePayment(this.payment))
    },
    async submit () {
      await this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.$emit('savePayment', cloneDeep(this.form))
        this.$eventBus.$emit('CLOSE_MODAL')
        this.form = cloneDeep(this.preparePayment(this.payment))
        this.$v.form.$reset()
      } else {
        this.form.paymentMilestones.forEach((item, index) => {
          if (this.$v.form.paymentMilestones.$each[index].$invalid) {
            const messages = errorMessageGenerator(
              this.$v.form.paymentMilestones.$each[index].paymentPart,
              'paymentPart'
            )
            item.errorMessage = messages.length ? messages[0] : null
          }
        })
        this.key = this.key + 1
      }
    }
  },
  watch: {
    payment (v) {
      this.form = cloneDeep(this.preparePayment(v))
    },
    paymentMilestones (v, p) {
      if (v.length > p.length && !v[v.length - 1].paymentPart) {
        v[v.length - 1].paymentPart = v[v.length - 1].percentToPay
        v[v.length - 1].partAmountCents =
          v[v.length - 1].paymentAmountCents *
          (Number(v[v.length - 1].paymentPart) / 100)
      }
      this.form.paymentAmountCents = this.form.paymentMilestones.reduce(
        (acc, curr) => acc + (Number(curr.partAmountCents) || 0),
        0
      )
    },
    'form.paymentAmountCents' (v) {
      this.paymentCentsPerCurrency = this.calculateCentsPerCurrency(this.form.paymentMilestones)
    }
  },
  validations: paymentApplicationModel
}
</script>

<style scoped>

.error-message {
  position: absolute;
  right: 3rem;
  padding: 0.25rem;
  z-index: 10;
}

</style>
